import React, { useMemo } from "react";
import { useSettings } from "@/contexts/SettingsContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Heart, Palette, Star, Truck, ShieldCheck, MapPin, UserCheck, Smile } from "lucide-react";
import SectionAccents from "@/components/SectionAccents";

const WHY_ITEMS = [
    { icon: Heart, text: "100% Personalized Gifts" },
    { icon: Palette, text: "Handcrafted with Attention to Detail" },
    { icon: Star, text: "Premium Quality Materials" },
    { icon: Truck, text: "Pan-India Delivery" },
    { icon: ShieldCheck, text: "Secure Payments" },
    { icon: UserCheck, text: "Professional Artists" },
    { icon: MapPin, text: "Perfect for Every Occasion" },
    { icon: Smile, text: "Dedicated Customer Support" },
];

const FAQs = () => {
    const { settings } = useSettings();
    const faqs = settings?.faqs || [];

    const grouped = useMemo(() => {
        const map = new Map();
        faqs.forEach((f, idx) => {
            const cat = f.category || "General";
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push({ ...f, _idx: idx });
        });
        return Array.from(map.entries());
    }, [faqs]);

    return (
        <div className="relative max-w-3xl mx-auto px-6 py-14 overflow-hidden" data-testid="faqs-page">
            <SectionAccents variant="faqs" />
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Frequently asked</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter mt-1">Every answer, ready.</h1>
            <p className="text-muted-foreground mt-3 max-w-xl">Everything you need to know about ordering, artwork, delivery and support for your Cherishables gift.</p>

            <div className="mt-10 space-y-8">
                {grouped.map(([cat, items]) => (
                    <section key={cat} data-testid={`faq-section-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <h2 className="font-display text-2xl font-semibold tracking-tight mb-3">{cat}</h2>
                        <div className="rounded-3xl glass p-2">
                            <Accordion type="single" collapsible className="w-full">
                                {items.map((f) => (
                                    <AccordionItem key={f.id || f._idx} value={`f-${f._idx}`} className="px-4">
                                        <AccordionTrigger className="text-left text-base font-semibold">{f.question}</AccordionTrigger>
                                        <AccordionContent className="text-muted-foreground whitespace-pre-line">{f.answer}</AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </div>
                    </section>
                ))}
                {faqs.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No FAQs yet.</div>}
            </div>

            <section className="mt-16" data-testid="why-choose-cherishables">
                <h2 className="font-display text-3xl font-semibold tracking-tight text-center">Why choose Cherishables?</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                    {WHY_ITEMS.map((w, i) => (
                        <div key={i} className="rounded-2xl glass p-4 flex flex-col items-center text-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                                <w.icon className="w-5 h-5" />
                            </div>
                            <div className="text-xs font-semibold leading-tight">{w.text}</div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default FAQs;
